import { motion } from "framer-motion";
import { Phone, ShieldAlert, EyeOff, HeartHandshake, Scale } from "lucide-react";

const HELP = [
  { icon: HeartHandshake, title: "Someone to talk to", text: "Yarn confidentially with a worker who understands culture, family and community." },
  { icon: ShieldAlert, title: "Safety planning", text: "Work out a plan to keep you and your children safe — at home, leaving, or after." },
  { icon: Scale, title: "Getting connected", text: "Help reaching safe housing, legal, court, health and Centrelink support." },
];

const LINES = [
  ["Emergency", "000", "000"],
  ["1800RESPECT", "1800 737 732", "1800737732"],
  ["NSW DV Line", "1800 65 64 63", "1800656463"],
  ["13YARN", "13 92 76", "139276"],
];

export const DVSupport = ({ onRequest }) => (
  <section id="domestic-violence" data-testid="dv-section" className="scroll-mt-24 bg-sand px-5 pt-20 sm:px-10">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-[1440px] rounded-[32px] bg-ochre-dark p-8 text-sand sm:p-12"
    >
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="eyebrow text-sand/85">Family &amp; domestic violence support</p>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1] tracking-tight sm:text-6xl">You are not alone, and it is <span className="italic">not your fault.</span></h2>
          <p className="mt-5 max-w-xl text-sand/90">If someone is hurting, controlling or frightening you or your children, we can help — confidentially, at your pace, and in a culturally safe way.</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {HELP.map((h) => (
              <div key={h.title} className="border-t border-sand/30 pt-4">
                <h.icon className="h-5 w-5" />
                <p className="mt-3 font-semibold">{h.title}</p>
                <p className="mt-1 text-sm text-sand/85">{h.text}</p>
              </div>
            ))}
          </div>
          <button data-testid="service-request-domestic-violence" onClick={() => onRequest("Domestic violence")} className="mt-10 inline-flex items-center gap-3 rounded-full bg-sand px-7 py-4 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-sand">
            Ask for help privately
          </button>
        </div>
        <div className="space-y-4 lg:col-span-5">
          <div className="rounded-2xl bg-ink p-5 sm:p-6">
            <p className="eyebrow text-amber">Call any time · 24/7</p>
            <div className="mt-4 divide-y divide-sand/15">
              {LINES.map(([n, num, tel]) => (
                <a key={n} href={`tel:${tel}`} data-testid={`dv-call-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group flex items-center justify-between gap-3 py-3">
                  <span className="text-sm text-sand/85">{n}</span>
                  <span className="flex items-center gap-2 whitespace-nowrap font-display text-xl transition-colors group-hover:text-ochre sm:text-2xl"><Phone className="h-4 w-4" />{num}</span>
                </a>
              ))}
            </div>
          </div>
          <div data-testid="dv-online-safety" className="rounded-2xl border border-sand/30 p-6">
            <p className="flex items-center gap-2 font-semibold"><EyeOff className="h-4 w-4" /> Staying safe online</p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-sand/90">
              <li>Press <span className="font-mono">Esc</span> or tap Quick exit to leave this site instantly.</li>
              <li>Use a private / incognito window, or clear your browser history after visiting.</li>
              <li>If your phone may be monitored, call from a safe phone or a friend's.</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  </section>
);
